/**

 * Owen per-answer analysis — short insights after each borrower response.

 * Used by the conversational apply UI (no pre-question coach copy).

 */

import { OWEN_INSIGHTS, OWEN_EXPLANATIONS } from '../config/owenAI';
import { getLegacyAnswerReaction, pickVariedSidebarAck } from '../config/owenApplyReactions';



export function analyzeLegacyAnswer(questionId, answers) {

  const purpose = answers.purchaseOrRefinance;

  const estimatedValue = parseFloat(answers.estimatedValue);

  const downPayment = parseFloat(answers.downPayment);

  const annualIncome = parseFloat(answers.annualIncome);

  const monthlyDebts = parseFloat(answers.monthlyDebts);

  const creditScore = answers.creditScore;



  switch (questionId) {

    case 'purchaseOrRefinance':

      return purpose === 'purchase'

        ? "Purchase — I'll ask about down payment and first-time buyer options next."

        : "Refinance — I'll map rate-term, cash-out, and streamline paths for you.";



    case 'refiGoal':

      return "Got it — I'll narrow to programs that fit this refinance goal.";



    case 'propertyLocation':

      return "Good to know — I'll factor local limits and program rules from here.";



    case 'propertyCounty':

      return "County confirmed — I'm using it for loan limit estimates.";



    case 'estimatedValue':

      if (estimatedValue > 766550) {

        return `At $${estimatedValue.toLocaleString()}, I'll include jumbo programs in the mix.`;

      }

      if (estimatedValue > 0) {

        return `$${estimatedValue.toLocaleString()} — that keeps the widest program set open for now.`;

      }

      return "Value noted — I'll use it for LTV on the next questions.";



    case 'downPayment':

      if (downPayment > 0 && estimatedValue > 0) {

        const pct = (downPayment / estimatedValue) * 100;

        if (purpose === 'refinance') {

          return `Roughly ${pct.toFixed(0)}% LTV — your equity position guides what I can suggest.`;

        }

        if (pct >= 20) return `${pct.toFixed(0)}% down — you're in PMI-free conventional territory.`;

        return `${pct.toFixed(0)}% down — FHA and low-down conventional stay in play.`;

      }

      return purpose === 'refinance'

        ? "Payoff noted — I'll use it as the loan amount for estimates."

        : "Down payment noted — I'll check PMI and program minimums next.";



    case 'creditScore': {

      if (!creditScore) return 'Credit band saved — rate tiers depend on this range.';

      const num = parseInt(creditScore, 10);

      if (num >= 760 || String(creditScore).includes('760')) return OWEN_INSIGHTS.creditScore.excellent;

      if (num >= 700 || String(creditScore).includes('700')) return OWEN_INSIGHTS.creditScore.good;

      if (num >= 640 || String(creditScore).includes('640')) return OWEN_INSIGHTS.creditScore.fair;

      return OWEN_INSIGHTS.creditScore.building;

    }



    case 'monthlyDebts':

      if (monthlyDebts > 0 && annualIncome > 0) {

        const dti = (monthlyDebts * 12) / annualIncome;

        if (dti < 0.36) return `DTI near ${(dti * 100).toFixed(0)}% — that's strong for most programs.`;

        if (dti < 0.43) return `DTI near ${(dti * 100).toFixed(0)}% — still within typical guidelines.`;

        return `DTI near ${(dti * 100).toFixed(0)}% — I'll lean toward more flexible programs.`;

      }

      return 'Debt load noted — DTI will guide my next refinements.';



    case 'militaryService':

      if (['active', 'veteran', 'yes'].includes(answers.militaryService)) {

        return 'VA eligibility opens up — 0% down and no PMI are on the table for you.';

      }

      return "No VA path here — I'll continue with conventional and government options.";



    case 'loanSizeTier':

      return "Loan size tier set — I'll filter conforming vs jumbo next.";



    case 'propertyUse':

      return OWEN_EXPLANATIONS.propertyUse || 'Occupancy drives rate and down payment rules — noted.';



    default:

      if (OWEN_EXPLANATIONS[questionId]) return OWEN_EXPLANATIONS[questionId];

      return getLegacyAnswerReaction(questionId, answers);

  }

}



export function analyzeV1Answer(questionId, answers) {

  const goal = answers.goal;



  switch (questionId) {

    case 'goal':

      return goal === 'purchase'

        ? "Purchase — I'll ask about price and down payment next."

        : "Refinance — I'll learn your current loan type next.";



    case 'primaryResidence':

      return answers.primaryResidence === 'yes'

        ? 'Primary residence — V1 preparation paths apply.'

        : 'Non-primary use — this version focuses on owner-occupied loans.';



    case 'citizenship':

      return answers.citizenship === 'other'

        ? 'Non-citizen path — V1 is limited to citizens and permanent residents.'

        : 'Citizenship confirmed — continuing scope check.';



    case 'incomeType':

      return answers.incomeType === 'w2'

        ? 'W-2 income — good fit for the six V1 preparation flows.'

        : 'Self-employed — V1 is W-2 only for now; I may suggest a handoff.';



    case 'propertyType':

      return answers.propertyType === 'sfr'

        ? 'Detached SFR — widest conventional and FHA fit.'

        : 'Non-SFR — V1 currently targets single-family detached.';



    case 'creditBand': {

      const num = parseInt(answers.creditBand, 10);

      if (num >= 740) return 'Strong credit — conventional minimum down payments apply.';

      if (num >= 620) return 'Credit score noted — FHA and conventional paths stay in play.';

      if (num >= 580) return 'Credit in FHA range — 3.5% down is the typical floor.';

      return 'Credit band saved — down payment minimums depend on this range.';

    }



    case 'purchasePrice':

      return answers.purchasePrice

        ? 'Price noted — pairing with down payment for program fit.'

        : 'Price captured.';



    case 'downPayment': {

      const price = parseFloat(String(answers.purchasePrice || '').replace(/,/g, ''));

      const down = parseFloat(String(answers.downPayment || '').replace(/,/g, ''));

      if (price > 0 && down > 0) {

        const pct = (down / price) * 100;

        return `About ${pct.toFixed(0)}% down — checking FHA vs conventional floors.`;

      }

      return 'Down payment noted.';

    }



    case 'currentLoanType':

      return 'Current loan type sets streamline vs full refi paths.';



    case 'refiGoal':

      return answers.refiGoal === 'cash-out'

        ? 'Cash-out goal — conventional cash-out may fit.'

        : 'Rate/payment goal — streamline or rate-term paths next.';



    default:

      return pickVariedSidebarAck(questionId, answers[questionId]);

  }

}



export function analyzeFieldAnswer(questionId) {

  const hints = {

    legalName: 'Identity anchor for your file.',

    email: 'Lenders you forward to will reach you here.',

    currentAddress: 'Address helps verify occupancy and subject property context.',

    occupancyAttestation: 'Primary occupancy attestation supports agency eligibility.',

  };

  return hints[questionId] || pickVariedSidebarAck(questionId, questionId);

}


